import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useMutation, useQuery, useQueryClient, UseMutationResult } from "@tanstack/react-query";
import { useForm, Controller, FieldError } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useEffect, useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import type { User, AuditLog, DashboardStats, PromoCode, SponsoredAdPackage, Clip } from "@shared/schema";
import { insertPromoCodeSchema, updatePromoCodeSchema, insertSponsoredAdPackageSchema, updateSponsoredAdPackageSchema } from "@shared/schema";

// --- Zod Schemas for Validation ---
const settingsSchema = z.object({
  robloxCookie: z.string().min(1, "Roblox cookie cannot be empty"),
  tokens_per_robux: z.preprocess((v) => Number(v), z.number().min(0, "Value must be positive")),
  withdrawals_enabled: z.boolean(),
  clipping_tokens_per_view: z.preprocess((v) => Number(v), z.number().min(0, "Value must be positive")), // Add this line
});

const adminUpdateUserSchema = z.object({
  isBanned: z.boolean().optional(),
  tokenBalance: z.number().int().optional(),
});

// --- API Fetcher Functions ---
const fetchAdminSettings = async () => {
  const res = await fetch("/api/admin/settings");
  if (!res.ok) throw new Error("Failed to fetch settings");
  return res.json();
};

const fetchDashboardStats = async (): Promise<DashboardStats> => {
  const res = await fetch("/api/admin/dashboard-stats");
  if (!res.ok) throw new Error("Failed to fetch dashboard stats");
  return res.json();
};

const fetchAllUsers = async (): Promise<User[]> => {
  const res = await fetch("/api/admin/users");
  if (!res.ok) throw new Error("Failed to fetch users");
  return res.json();
};

const fetchAuditLogs = async (): Promise<AuditLog[]> => {
  const res = await fetch("/api/admin/audit-logs");
  if (!res.ok) throw new Error("Failed to fetch audit logs");
  return res.json();
};

const fetchAllPromoCodes = async (): Promise<PromoCode[]> => {
  const res = await fetch("/api/admin/promocodes");
  if (!res.ok) throw new Error("Failed to fetch promo codes");
  return res.json();
};

// Add this block after the fetchAllPromoCodes function (pending clips API)
const fetchPendingClips = async (): Promise<Clip[]> => {
  const res = await fetch("/api/admin/clipping/pending");
  if (!res.ok) throw new Error("Failed to fetch pending clips");
  return res.json();
};

const approveClip = async (clipId: string) => {
  const res = await fetch(`/api/admin/clipping/${clipId}/approve`, { method: "POST" });
  if (!res.ok) throw new Error("Failed to approve clip");
  return res.json();
};

const rejectClip = async ({ clipId, rejectionReason }: { clipId: string; rejectionReason: string }) => {
  const res = await fetch(`/api/admin/clipping/${clipId}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rejectionReason }),
  });
  if (!res.ok) throw new Error("Failed to reject clip");
  return res.json();
};

const updateSettings = async (data: Partial<z.infer<typeof settingsSchema>>) => {
  const res = await fetch("/api/admin/settings", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error("Failed to save settings");
  return res.json();
};

const updateUser = async ({ id, ...data }: { id: string } & Partial<z.infer<typeof adminUpdateUserSchema>>) => {
  const res = await fetch(`/api/admin/users/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to update user");
  }
  return res.json();
};

const createPromoCode = async (data: z.infer<typeof insertPromoCodeSchema>) => {
  const res = await fetch("/api/admin/promocodes", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const respData = await res.json();
  if (!res.ok) throw new Error(respData.message || "Failed to create promo code");
  return respData;
};

const updatePromoCode = async ({ id, ...data }: { id: string } & z.infer<typeof updatePromoCodeSchema>) => {
  const res = await fetch(`/api/admin/promocodes/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const respData = await res.json();
  if (!res.ok) throw new Error(respData.message || "Failed to update promo code");
  return respData;
};

const deletePromoCode = async (id: string) => {
  const res = await fetch(`/api/admin/promocodes/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete promo code");
};

// --- Sponsored Ad Package API Fetchers ---
const fetchSponsoredAdPackages = async (): Promise<SponsoredAdPackage[]> => {
  const res = await fetch("/api/admin/sponsored-ads/packages");
  if (!res.ok) throw new Error("Failed to fetch ad packages");
  return res.json();
};

const createSponsoredAdPackage = async (data: z.infer<typeof insertSponsoredAdPackageSchema>) => {
  const res = await fetch("/api/admin/sponsored-ads/packages", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const respData = await res.json();
  if (!res.ok) throw new Error(respData.message || "Failed to create ad package");
  return respData;
};

const updateSponsoredAdPackage = async ({ id, ...data }: { id: string } & z.infer<typeof updateSponsoredAdPackageSchema>) => {
  const res = await fetch(`/api/admin/sponsored-ads/packages/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  const respData = await res.json();
  if (!res.ok) throw new Error(respData.message || "Failed to update ad package");
  return respData;
};

const deleteSponsoredAdPackage = async (id: string) => {
  const res = await fetch(`/api/admin/sponsored-ads/packages/${id}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error("Failed to delete ad package");
};

const AdminPage = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isManageUserOpen, setManageUserOpen] = useState(false);
  const [isPromoCodeDialogOpen, setPromoCodeDialogOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);
  const [editedTokenBalance, setEditedTokenBalance] = useState<number>(0);

  // Ad package state
  const [isAdPackageDialogOpen, setAdPackageDialogOpen] = useState(false);
  const [editingAdPackage, setEditingAdPackage] = useState<SponsoredAdPackage | null>(null);

  // Rejection dialog state for clips
  const [rejectionClip, setRejectionClip] = useState<Clip | null>(null);

  // --- API Queries using React Query ---
  const { data: settings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ["adminSettings"],
    queryFn: fetchAdminSettings,
  });

  const { data: stats, isLoading: isLoadingStats } = useQuery({
    queryKey: ["dashboardStats"],
    queryFn: fetchDashboardStats,
  });

  const { data: users, isLoading: isLoadingUsers, error: usersError } = useQuery({
    queryKey: ["adminUsers"],
    queryFn: fetchAllUsers,
  });

  const { data: auditLogs, isLoading: isLoadingAuditLogs } = useQuery({
    queryKey: ["adminAuditLogs"],
    queryFn: fetchAuditLogs,
  });

  const { data: promoCodes, isLoading: isLoadingPromoCodes } = useQuery({
    queryKey: ["adminPromoCodes"],
    queryFn: fetchAllPromoCodes,
  });

  const { data: adPackages, isLoading: isLoadingAdPackages } = useQuery({
    queryKey: ["adminAdPackages"],
    queryFn: fetchSponsoredAdPackages,
  });

  const { data: pendingClips, isLoading: isLoadingPendingClips } = useQuery({
    queryKey: ["pendingClips"],
    queryFn: fetchPendingClips,
  });

  const userMap = useMemo(() => {
    if (!users) return new Map<string, string>();
    return new Map(users.map((u) => [u.id, u.username]));
  }, [users]);

  // --- Mutations ---
  const settingsMutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSettings"] });
      toast({ title: "Success", description: "Settings updated successfully!" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const userUpdateMutation = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminUsers"] });
      toast({ title: "Success", description: "User updated successfully!" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const promoCodeCreateMutation = useMutation({
    mutationFn: createPromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminPromoCodes"] });
      queryClient.invalidateQueries({ queryKey: ["publicPromoCodes"] });
      toast({ title: "Success", description: "Promo code created!" });
      setPromoCodeDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const promoCodeUpdateMutation = useMutation({
    mutationFn: updatePromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminPromoCodes"] });
      queryClient.invalidateQueries({ queryKey: ["publicPromoCodes"] });
      toast({ title: "Success", description: "Promo code updated!" });
      setPromoCodeDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const promoCodeDeleteMutation = useMutation({
    mutationFn: deletePromoCode,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminPromoCodes"] });
      queryClient.invalidateQueries({ queryKey: ["publicPromoCodes"] });
      toast({ title: "Success", description: "Promo code deleted!" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const adPackageCreateMutation = useMutation({
    mutationFn: createSponsoredAdPackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminAdPackages"] });
      toast({ title: "Success", description: "Ad package created!" });
      setAdPackageDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const adPackageUpdateMutation = useMutation({
    mutationFn: updateSponsoredAdPackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminAdPackages"] });
      toast({ title: "Success", description: "Ad package updated!" });
      setAdPackageDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const adPackageDeleteMutation = useMutation({
    mutationFn: deleteSponsoredAdPackage,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminAdPackages"] });
      toast({ title: "Success", description: "Ad package deleted!" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const clipApproveMutation = useMutation({
    mutationFn: approveClip,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pendingClips"] });
      toast({ title: "Success", description: "Clip approved!" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const clipRejectMutation = useMutation({
    mutationFn: rejectClip,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pendingClips"] });
      setRejectionClip(null); // Close the dialog on success
      toast({ title: "Success", description: "Clip rejected!" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error?.message ?? String(error), variant: "destructive" });
    },
  });

  const { control, handleSubmit, setValue } = useForm({
    resolver: zodResolver(settingsSchema),
    defaultValues: { robloxCookie: "", tokens_per_robux: 2, withdrawals_enabled: true, clipping_tokens_per_view: 0.5 },
  });

  useEffect(() => {
    if (settings) {
      setValue("robloxCookie", settings.robloxCookie || "");
      setValue("tokens_per_robux", settings.tokens_per_robux || 2);
      setValue("withdrawals_enabled", settings.withdrawals_enabled !== undefined ? settings.withdrawals_enabled : true);
      setValue("clipping_tokens_per_view", settings.clipping_tokens_per_view || 1); // Add this line
    }
  }, [settings, setValue]);

  useEffect(() => {
    if (selectedUser && users) {
      const freshUserData = users.find((u) => u.id === selectedUser.id);
      if (freshUserData) {
        setSelectedUser(freshUserData);
        setEditedTokenBalance(freshUserData.tokenBalance);
      }
    }
  }, [users, selectedUser]);

  const filteredUsers = users?.filter(
    (user) =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleManageUser = (user: User) => {
    setSelectedUser(user);
    setEditedTokenBalance(user.tokenBalance);
    setManageUserOpen(true);
  };

  const handleBanToggle = () => {
    if (!selectedUser) return;
    userUpdateMutation.mutate({ id: selectedUser.id, isBanned: !selectedUser.isBanned });
  };

  const handleSaveTokenBalance = () => {
    if (!selectedUser || editedTokenBalance === undefined) return;
    userUpdateMutation.mutate({ id: selectedUser.id, tokenBalance: editedTokenBalance });
  };

  const handleOpenPromoCodeDialog = (promo: PromoCode | null) => {
    setEditingPromoCode(promo);
    setPromoCodeDialogOpen(true);
  };

  const handleOpenAdPackageDialog = (pkg: SponsoredAdPackage | null) => {
    setEditingAdPackage(pkg);
    setAdPackageDialogOpen(true);
  };

  return (
    <div className="w-full p-4 sm:p-6 md:p-8">
      <h1 className="text-3xl font-bold mb-6">Admin Panel</h1>
      <Tabs defaultValue="dashboard" className="flex flex-col">
        <TabsList className="mb-6 self-start flex-wrap h-auto">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="promo-codes">Promo Codes</TabsTrigger>
          <TabsTrigger value="ad-packages">Ad Packages</TabsTrigger>
          <TabsTrigger value="audit-log">Audit Log</TabsTrigger>
          <TabsTrigger value="clipping">Clipping</TabsTrigger> {/* Added Clipping tab trigger */}
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoadingStats ? <Skeleton className="h-8 w-1/2" /> : <div className="text-2xl font-bold">{stats?.totalUsers ?? "N/A"}</div>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Withdrawals</CardTitle>
              </CardHeader>
              <CardContent>{isLoadingStats ? <Skeleton className="h-8 w-1/2" /> : <div className="text-2xl font-bold">{stats?.pendingWithdrawals ?? "N/A"}</div>}</CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Completed Withdrawals</CardTitle>
              </CardHeader>
              <CardContent>{isLoadingStats ? <Skeleton className="h-8 w-1/2" /> : <div className="text-2xl font-bold">{stats?.completedWithdrawals ?? "N/A"}</div>}</CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="users">
          <Card>
            <CardHeader>
              <CardTitle>Users</CardTitle>
              <div className="mt-4">
                <Input placeholder="Search by username or display name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingUsers ? (
                <p>Loading users...</p>
              ) : usersError ? (
                <p className="text-destructive">{(usersError as any).message}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>Display Name</TableHead>
                      <TableHead>Tokens</TableHead>
                      <TableHead>Verified</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers?.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>{user.username}</TableCell>
                        <TableCell>{user.displayName}</TableCell>
                        <TableCell>{user.tokenBalance}</TableCell>
                        <TableCell>{user.isVerified ? <Badge>Yes</Badge> : <Badge variant="secondary">No</Badge>}</TableCell>
                        <TableCell>{user.isBanned ? <Badge variant="destructive">Banned</Badge> : <Badge>Active</Badge>}</TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" onClick={() => handleManageUser(user)}>
                            Manage
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="promo-codes">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Promo Codes</CardTitle>
                <Button onClick={() => handleOpenPromoCodeDialog(null)}>Create New</Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingPromoCodes ? (
                <p>Loading promo codes...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Reward</TableHead>
                      <TableHead>Uses</TableHead>
                      <TableHead>Max Uses</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {promoCodes?.map((promo) => (
                      <TableRow key={promo.id}>
                        <TableCell>{promo.code}</TableCell>
                        <TableCell>{promo.reward}</TableCell>
                        <TableCell>{promo.uses}</TableCell>
                        <TableCell>{promo.maxUses}</TableCell>
                        <TableCell>{promo.expiresAt ? new Date(promo.expiresAt).toLocaleDateString() : "Never"}</TableCell>
                        <TableCell className="space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleOpenPromoCodeDialog(promo)}>
                            Edit
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => promoCodeDeleteMutation.mutate(promo.id)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ad-packages">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Sponsored Ad Packages</CardTitle>
                <Button onClick={() => handleOpenAdPackageDialog(null)}>Create New Package</Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingAdPackages ? (
                <p>Loading packages...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Tokens</TableHead>
                      <TableHead>Price (USD)</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adPackages?.map((pkg) => (
                      <TableRow key={pkg.id}>
                        <TableCell>{pkg.name}</TableCell>
                        <TableCell>{pkg.tokenAmount.toLocaleString()}</TableCell>
                        <TableCell>${pkg.price.toFixed(2)}</TableCell>
                        <TableCell>{pkg.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge>}</TableCell>
                        <TableCell className="space-x-2">
                          <Button variant="outline" size="sm" onClick={() => handleOpenAdPackageDialog(pkg)}>
                            Edit
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => adPackageDeleteMutation.mutate(pkg.id)}>
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="audit-log">
          <Card>
            <CardHeader>
              <CardTitle>Audit Log</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingAuditLogs ? (
                <p>Loading audit logs...</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs?.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                        <TableCell>{log.userId ? userMap.get(log.userId) || log.userId : "System"}</TableCell>
                        <TableCell>{log.action}</TableCell>
                        <TableCell>
                          <pre className="whitespace-pre-wrap break-all text-xs">{JSON.stringify(log.details, null, 2)}</pre>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Clipping tab content */}
        <TabsContent value="clipping">
          <ClippingPanel clips={pendingClips} isLoading={isLoadingPendingClips} approveMutation={clipApproveMutation} onReject={(clip) => setRejectionClip(clip)} userMap={userMap} />
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingSettings ? (
                <p>Loading settings...</p>
              ) : (
                <form onSubmit={handleSubmit((data) => settingsMutation.mutate(data))} className="space-y-6">
                  <div className="space-y-2">
                    <label htmlFor="robloxCookie">Roblox Cookie</label>
                    <Controller name="robloxCookie" control={control} render={({ field }) => <Input id="robloxCookie" {...field} type="password" />} />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="tokens_per_robux">Tokens per Robux</label>
                    <Controller name="tokens_per_robux" control={control} render={({ field }) => <Input id="tokens_per_robux" type="number" {...field} />} />
                  </div>
                  <div className="flex items-center space-x-4 p-4 border rounded-lg">
                    <Controller
                      name="withdrawals_enabled"
                      control={control}
                      render={({ field }) => (
                        <Switch id="withdrawals-enabled-switch" checked={field.value} onCheckedChange={field.onChange} />
                      )}
                    />
                    <div>
                      <label htmlFor="withdrawals-enabled-switch">Enable Withdrawals</label>
                      <p className="text-sm text-muted-foreground">When disabled, users will not be able to submit new withdrawal requests.</p>
                    </div>
                  </div>

                  {/* New clipping tokens input */}
                  <div className="space-y-2">
                    <label htmlFor="clipping_tokens_per_view">Tokens per 1000 Views</label>
                    <Controller
                      name="clipping_tokens_per_view"
                      control={control}
                      render={({ field }) => <Input id="clipping_tokens_per_view" type="number" {...field} />}
                    />
                  </div>

                  <Button type="submit" disabled={settingsMutation.isPending}>
                    {settingsMutation.isPending ? "Saving..." : "Save Settings"}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={isManageUserOpen} onOpenChange={setManageUserOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manage {selectedUser?.username}</DialogTitle>
            <DialogDescription>View and manage user details.</DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div>
                <h4 className="font-semibold">User ID</h4>
                <p className="text-sm text-muted-foreground">{selectedUser.id}</p>
              </div>
              <div>
                <h4 className="font-semibold">Token Balance</h4>
                <div className="flex items-center space-x-2">
                  <Input type="number" value={editedTokenBalance} onChange={(e) => setEditedTokenBalance(Number(e.target.value))} />
                  <Button onClick={handleSaveTokenBalance} disabled={userUpdateMutation.isPending && (userUpdateMutation.variables as any)?.tokenBalance !== undefined}>
                    Save
                  </Button>
                </div>
              </div>
              <div>
                <h4 className="font-semibold">Ban Status</h4>
                <p>
                  The user is currently{" "}
                  <span className={selectedUser.isBanned ? "text-destructive font-bold" : "font-bold"}>{selectedUser.isBanned ? "Banned" : "Active"}</span>.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleBanToggle} variant={selectedUser?.isBanned ? "secondary" : "destructive"} disabled={userUpdateMutation.isPending && (userUpdateMutation.variables as any)?.isBanned !== undefined}>
              {selectedUser?.isBanned ? "Unban User" : "Ban User"}
            </Button>
            <Button variant="outline" onClick={() => setManageUserOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PromoCodeDialog isOpen={isPromoCodeDialogOpen} setIsOpen={setPromoCodeDialogOpen} promoCode={editingPromoCode} createMutation={promoCodeCreateMutation} updateMutation={promoCodeUpdateMutation} />

      <SponsoredAdPackageDialog isOpen={isAdPackageDialogOpen} setIsOpen={setAdPackageDialogOpen} adPackage={editingAdPackage} createMutation={adPackageCreateMutation} updateMutation={adPackageUpdateMutation} />

      {/* Reject clip dialog component usage */}
      <RejectClipDialog clip={rejectionClip} isOpen={!!rejectionClip} onClose={() => setRejectionClip(null)} rejectMutation={clipRejectMutation} />
    </div>
  );
};

export default AdminPage;

// --- Clipping Panel Component ---

interface ClippingPanelProps {
  clips: Clip[] | undefined;
  isLoading: boolean;
  approveMutation: UseMutationResult<any, Error, string, unknown>;
  onReject: (clip: Clip) => void;
  userMap: Map<string, string>;
}

const ClippingPanel: React.FC<ClippingPanelProps> = ({ clips, isLoading, approveMutation, onReject, userMap }) => {
  if (isLoading) return <p>Loading pending clips...</p>;
  if (!clips || clips.length === 0) return <p>No pending clips to review.</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Pending Clips for Review</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Video</TableHead>
              <TableHead>Submitter</TableHead>
              <TableHead>Submitted At</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clips.map((clip) => (
              <TableRow key={clip.id}>
                <TableCell>
                  <a href={clip.videoUrl} target="_blank" rel="noopener noreferrer" className="hover:underline text-primary">
                    {clip.videoTitle}
                  </a>
                </TableCell>
                <TableCell>{userMap.get(clip.userId) || clip.userId}</TableCell>
                <TableCell>{new Date(clip.createdAt).toLocaleString()}</TableCell>
                <TableCell className="space-x-2">
                  <Button size="sm" onClick={() => approveMutation.mutate(clip.id)} disabled={approveMutation.isPending}>
                    Approve
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => onReject(clip)}>
                    Reject
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

// --- Reject Clip Dialog ---

const rejectClipFormSchema = z.object({
  rejectionReason: z.string().min(1, "Reason is required"),
});

type RejectClipFormData = z.infer<typeof rejectClipFormSchema>;

interface RejectClipDialogProps {
  clip: Clip | null;
  isOpen: boolean;
  onClose: () => void;
  rejectMutation: UseMutationResult<any, Error, { clipId: string; rejectionReason: string }, unknown>;
}

const RejectClipDialog: React.FC<RejectClipDialogProps> = ({ clip, isOpen, onClose, rejectMutation }) => {
    const { register, handleSubmit, reset } = useForm<RejectClipFormData>({
        resolver: zodResolver(rejectClipFormSchema), 
    });

  useEffect(() => {
    if (!isOpen) reset({ rejectionReason: "" });
  }, [isOpen, reset]);

  if (!clip) return null;

  const onSubmit = (data: RejectClipFormData) => {
    rejectMutation.mutate({ clipId: clip.id, rejectionReason: data.rejectionReason });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject Clip: {clip.videoTitle}</DialogTitle>
          <DialogDescription>Please provide a reason for rejecting this clip.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input {...register("rejectionReason")} placeholder="e.g., Video is private" />
          <DialogFooter>
            <Button type="submit" variant="destructive" disabled={rejectMutation.isPending}>
              {rejectMutation.isPending ? "Rejecting..." : "Confirm Rejection"}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Promo Code Dialog Component ---

type CreatePromoCodeVariables = z.infer<typeof insertPromoCodeSchema>;
type UpdatePromoCodeVariables = { id: string } & z.infer<typeof updatePromoCodeSchema>;

interface PromoCodeDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  promoCode: PromoCode | null;
  createMutation: UseMutationResult<any, Error, CreatePromoCodeVariables, unknown>;
  updateMutation: UseMutationResult<any, Error, UpdatePromoCodeVariables, unknown>;
}

// Form-specific schema to handle string-based date input
const promoCodeFormSchema = z.object({
  code: z.string().min(1, "Code is required"),
  reward: z.number().min(1, "Reward must be at least 1"),
  maxUses: z.number().min(0, "Max uses must be non-negative"),
  expiresAt: z.string().optional(),
  isPublic: z.boolean(),
});
type PromoCodeFormData = z.infer<typeof promoCodeFormSchema>;

const PromoCodeDialog: React.FC<PromoCodeDialogProps> = ({ isOpen, setIsOpen, promoCode, createMutation, updateMutation }) => {
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<PromoCodeFormData>({
    resolver: zodResolver(promoCodeFormSchema),
  });

  useEffect(() => {
    if (isOpen) {
      const defaultValues = promoCode
        ? {
            ...promoCode,
            expiresAt: promoCode.expiresAt ? new Date(promoCode.expiresAt).toISOString().split("T")[0] : "",
          }
        : {
            code: "",
            reward: 100,
            maxUses: 100,
            isPublic: true,
            expiresAt: "",
          };
      reset(defaultValues);
    }
  }, [isOpen, promoCode, reset]);

  const onSubmit = (data: PromoCodeFormData) => {
    const submissionData = {
      ...data,
      // Convert string back to Date object for the backend, or undefined if empty
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
    };

    if (promoCode) {
      updateMutation.mutate({ id: promoCode.id, ...submissionData });
    } else {
      createMutation.mutate(submissionData);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{promoCode ? "Edit Promo Code" : "Create Promo Code"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="code">Code</Label>
            <Input id="code" {...register("code")} />
            {errors.code && <p className="text-destructive text-sm">{errors.code.message}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="reward">Reward</Label>
            <Input id="reward" type="number" {...register("reward", { valueAsNumber: true })} />
            {errors.reward && <p className="text-destructive text-sm">{errors.reward.message}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="maxUses">Max Uses</Label>
            <Input id="maxUses" type="number" {...register("maxUses", { valueAsNumber: true })} />
            {errors.maxUses && <p className="text-destructive text-sm">{errors.maxUses.message}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="expiresAt">Expires At (Optional)</Label>
            <Input id="expiresAt" type="date" {...register("expiresAt")} />
            {errors.expiresAt && <p className="text-destructive text-sm">{errors.expiresAt.message}</p>}
          </div>
          <div className="flex items-center space-x-2">
            <Controller name="isPublic" control={control} render={({ field }) => <Switch id="isPublic" checked={field.value} onCheckedChange={field.onChange} />} />
            <Label htmlFor="isPublic">Publicly visible</Label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// --- Sponsored Ad Package Dialog Component ---

type CreateAdPackageVariables = z.infer<typeof insertSponsoredAdPackageSchema>;
type UpdateAdPackageVariables = { id: string } & z.infer<typeof updateSponsoredAdPackageSchema>;

interface AdPackageDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  adPackage: SponsoredAdPackage | null;
  createMutation: UseMutationResult<any, Error, CreateAdPackageVariables, unknown>;
  updateMutation: UseMutationResult<any, Error, UpdateAdPackageVariables, unknown>;
}

const adPackageFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  tokenAmount: z.number().min(1, "Token amount must be at least 1"),
  price: z.number().min(0.01, "Price must be positive"),
  isActive: z.boolean(),
});
type AdPackageFormData = z.infer<typeof adPackageFormSchema>;

const SponsoredAdPackageDialog: React.FC<AdPackageDialogProps> = ({ isOpen, setIsOpen, adPackage, createMutation, updateMutation }) => {
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm<AdPackageFormData>({
    resolver: zodResolver(adPackageFormSchema),
  });

  useEffect(() => {
    if (isOpen) {
      const defaultValues = adPackage
        ? { ...adPackage }
        : {
            name: "",
            tokenAmount: 1000,
            price: 1.0,
            isActive: true,
          };
      reset(defaultValues as AdPackageFormData);
    }
  }, [isOpen, adPackage, reset]);

  const onSubmit = (data: AdPackageFormData) => {
    if (adPackage) {
      updateMutation.mutate({ id: adPackage.id, ...data });
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{adPackage ? "Edit Ad Package" : "Create Ad Package"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="name">Package Name</Label>
            <Input id="name" {...register("name")} />
            {errors.name && <p className="text-destructive text-sm">{errors.name.message}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="tokenAmount">Token Amount</Label>
            <Input id="tokenAmount" type="number" {...register("tokenAmount", { valueAsNumber: true })} />
            {errors.tokenAmount && <p className="text-destructive text-sm">{errors.tokenAmount.message}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="price">Price (USD)</Label>
            <Input id="price" type="number" step="0.01" {...register("price", { valueAsNumber: true })} />
            {errors.price && <p className="text-destructive text-sm">{errors.price.message}</p>}
          </div>
          <div className="flex items-center space-x-2">
            <Controller name="isActive" control={control} render={({ field }) => <Switch id="isActive" checked={field.value} onCheckedChange={field.onChange} />} />
            <Label htmlFor="isActive">Active</Label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
              {createMutation.isPending || updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};